import React, { useState, useEffect } from 'react';
import './InviteModal.css';

type InviteCollaboratorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
  // When an admin invites on behalf of a user they're viewing, this is that
  // user's id. Omitted on a user's own page (owner is then the logged-in user).
  ownerUserId?: string;
};

type AccessLevel = 'view' | 'full';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ACCESS_LEVEL_HELP: Record<AccessLevel, string> = {
  view: 'View and download your documents.',
  full: 'View and download your documents (upload & workflows in a later phase).',
};

// Collaborator invite — the logged-in user invites a collaborator to their own
// document view. Role is fixed to "Collaborator"; the user only chooses the
// email and the access level. Backend: POST /invites/collaborator (owner is the
// logged-in user, derived server-side).
//   - new email       → an invite email is sent (user joins active, no approval)
//   - existing member → linked to your view immediately, no email
export default function InviteCollaboratorModal({
  isOpen,
  onClose,
  onDone,
  ownerUserId,
}: InviteCollaboratorModalProps) {
  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] = useState<AccessLevel>('view');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setAccessLevel('view');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = async () => {
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError(`"${trimmed}" is not a valid email address.`);
      return;
    }

    setSending(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/invites/collaborator`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, accessLevel, ...(ownerUserId ? { ownerUserId } : {}) }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to send collaborator invite.');

      // Success — refresh the list and close. The new collaborator (or the
      // pending "Invited" row) then appears in the table.
      onDone?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to send collaborator invite. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="smrtdocs-modal invite-modal">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Invite Collaborator</h2>
        </div>

        <div className="smrtdocs-modal-body">
          <p style={{ marginTop: 0, fontSize: '0.85rem', opacity: 0.8 }}>
            Invite someone to view these SMRT Docs documents as a collaborator.
          </p>

          <div className="invite-rows">
            <div className="invite-row">
              <input
                type="email"
                className="invite-email-input"
                placeholder="Email address"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                disabled={sending}
              />
              {/* Role is fixed for collaborator invites */}
              <select className="invite-role-select" value="Collaborator" disabled>
                <option value="Collaborator">Collaborator</option>
              </select>
            </div>

            <div className="invite-row" style={{ marginTop: 10 }}>
              <label style={{ fontSize: '0.85rem', minWidth: 90 }}>Access level</label>
              <select
                className="invite-role-select"
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value as AccessLevel)}
                disabled={sending}
              >
                <option value="view">View</option>
                <option value="full">Full</option>
              </select>
            </div>
            <p style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: 6 }}>
              {ACCESS_LEVEL_HELP[accessLevel]}
            </p>
          </div>

          {error && <p className="invite-error">{error}</p>}
        </div>

        <div className="smrtdocs-modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={sending}>
            Close
          </button>
          <button className="btn-save" onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : 'Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
